import React from "react";
import {Link} from "react-router-dom";
import {
    Accordion,
    Anchor,
    Badge,
    Button,
    Card,
    Group,
    Paper,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
    useMantineTheme
} from "@mantine/core";
import {IconExternalLink} from "@tabler/icons-react";
import type {VentureSection} from "../../ConfigContext";

type IconType = React.ElementType | undefined;

const SectionTitle = ({children}: { children: React.ReactNode }) => {
    const theme = useMantineTheme();
    return (
        <Title order={2} mb="md" c={theme.colors.brand[6]} style={{fontSize: '30px'}}>
            {children}
        </Title>
    );
};

const SectionIcon = ({icon, size = 28}: { icon: IconType; size?: number }) => {
    const theme = useMantineTheme();
    if (!icon) return null;
    return (
        <ThemeIcon variant="light" color="brand" size={size + 20} radius="xl">
            {React.createElement(icon, {size, stroke: 1.5, color: theme.colors.brand[6]})}
        </ThemeIcon>
    );
};

/**
 * Renders long-form copy using the config-wide convention: paragraphs joined by
 * "|", a leading "#" marking a bold heading.
 */
export const ProseSection = ({title, body}: { title?: string; body: string }) => (
    <Stack gap="xs">
        {title && <SectionTitle>{title}</SectionTitle>}
        {body.split("|").map((paragraph, index) =>
            paragraph.trimStart()[0] === "#" ? (
                <Text key={index} fw={800} mt="md" size="lg">
                    {paragraph.trim().replace(/^#/, '')}
                </Text>
            ) : (
                <Text key={index} mt="xs" size="md">
                    {paragraph.trim()}
                </Text>
            )
        )}
    </Stack>
);

export const FeatureGridSection = ({
    title,
    intro,
    items
}: {
    title: string;
    intro?: string;
    items: { icon?: React.ElementType; title: string; description: string }[];
}) => (
    <Stack>
        <SectionTitle>{title}</SectionTitle>
        {intro && <Text c="dimmed">{intro}</Text>}
        <SimpleGrid cols={{base: 1, sm: 2, lg: 3}} spacing="lg" mt="sm">
            {items.map((item) => (
                <Card key={item.title} withBorder radius="lg" padding="lg" shadow="sm">
                    <Stack gap="sm">
                        <SectionIcon icon={item.icon}/>
                        <Text fw={700} size="lg">{item.title}</Text>
                        <Text size="sm" c="dimmed">{item.description}</Text>
                    </Stack>
                </Card>
            ))}
        </SimpleGrid>
    </Stack>
);

export const StepsSection = ({
    title,
    intro,
    items
}: {
    title: string;
    intro?: string;
    items: { title: string; description: string }[];
}) => {
    const theme = useMantineTheme();
    return (
        <Stack>
            <SectionTitle>{title}</SectionTitle>
            {intro && <Text c="dimmed">{intro}</Text>}
            <SimpleGrid cols={{base: 1, md: items.length > 3 ? 2 : items.length}} spacing="lg" mt="sm">
                {items.map((item, index) => (
                    <Stack key={item.title} gap="xs">
                        <ThemeIcon variant="filled" color="brand.6" size={44} radius="xl">
                            <Text fw={700} c={theme.colors.gray[0]}>{index + 1}</Text>
                        </ThemeIcon>
                        <Text fw={700} size="lg">{item.title}</Text>
                        <Text size="sm" c="dimmed">{item.description}</Text>
                    </Stack>
                ))}
            </SimpleGrid>
        </Stack>
    );
};

export const UseCasesSection = ({
    title,
    intro,
    items
}: {
    title: string;
    intro?: string;
    items: { icon?: React.ElementType; title: string; description: string }[];
}) => (
    <Stack>
        <SectionTitle>{title}</SectionTitle>
        {intro && <Text c="dimmed">{intro}</Text>}
        <SimpleGrid cols={{base: 1, sm: 2, lg: 3}} spacing="md" mt="sm">
            {items.map((item) => (
                <Group key={item.title} align="flex-start" wrap="nowrap" gap="md">
                    <SectionIcon icon={item.icon} size={22}/>
                    <Stack gap={2}>
                        <Text fw={700}>{item.title}</Text>
                        <Text size="sm" c="dimmed">{item.description}</Text>
                    </Stack>
                </Group>
            ))}
        </SimpleGrid>
    </Stack>
);

export const FaqSection = ({
    title,
    items
}: {
    title: string;
    items: { question: string; answer: string }[];
}) => (
    <Stack>
        <SectionTitle>{title}</SectionTitle>
        <Accordion variant="separated" radius="md">
            {items.map((item) => (
                <Accordion.Item key={item.question} value={item.question}>
                    <Accordion.Control>{item.question}</Accordion.Control>
                    <Accordion.Panel>{item.answer}</Accordion.Panel>
                </Accordion.Item>
            ))}
        </Accordion>
    </Stack>
);

export const CtaSection = ({
    title,
    description,
    cta
}: {
    title: string;
    description?: string;
    cta: { label: string; link: string };
}) => {
    // A router Link cannot carry an absolute URL — it would navigate to
    // "/https://…" — so external targets render as a plain anchor.
    const isExternal = /^https?:\/\//.test(cta.link);

    return (
        <Paper withBorder radius="lg" p="xl" shadow="sm">
            <Stack align="center" gap="sm">
                <Title order={3} ta="center">{title}</Title>
                {description && <Text c="dimmed" ta="center">{description}</Text>}
                {isExternal ? (
                    <Button
                        component="a"
                        href={cta.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        radius="xl"
                        size="md"
                        color="brand.6"
                        mt="xs"
                        rightSection={<IconExternalLink size={16} stroke={1.6}/>}
                    >
                        {cta.label}
                    </Button>
                ) : (
                    <Button component={Link} to={cta.link} radius="xl" size="md" color="brand.6" mt="xs">
                        {cta.label}
                    </Button>
                )}
            </Stack>
        </Paper>
    );
};

export const VentureHero = ({
    name,
    tagline,
    summary,
    icon,
    status,
    website
}: {
    name: string;
    tagline: string;
    summary: string;
    icon: React.ElementType;
    status?: string;
    website?: {label: string; href: string; action?: string};
}) => {
    const theme = useMantineTheme();
    return (
        <Stack gap="md">
            <Group gap="md">
                <ThemeIcon variant="light" color="brand" size={64} radius="xl">
                    {React.createElement(icon, {size: 36, stroke: 1.5, color: theme.colors.brand[6]})}
                </ThemeIcon>
                <Stack gap={4}>
                    <Group gap="sm">
                        <Title order={1}>{name}</Title>
                        {status && <Badge color="accent" variant="light">{status}</Badge>}
                    </Group>
                    {website && (
                        <Anchor
                            href={website.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                        >
                            <Group gap={5} wrap="nowrap" align="center">
                                {website.label}
                                <IconExternalLink size={13} stroke={1.6}/>
                            </Group>
                        </Anchor>
                    )}
                </Stack>
            </Group>
            <Title order={2} c={theme.colors.brand[6]} style={{fontSize: '34px', maxWidth: '20ch'}}>
                {tagline}
            </Title>
            <Text size="lg" maw={720}>{summary}</Text>
            {website && (
                <Group>
                    <Button
                        component="a"
                        href={website.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        radius="xl"
                        size="md"
                        color="brand.6"
                        rightSection={<IconExternalLink size={16} stroke={1.6}/>}
                    >
                        {website.action ?? `Visit ${website.label}`}
                    </Button>
                </Group>
            )}
        </Stack>
    );
};

export const renderSection = (section: VentureSection, index: number) => {
    switch (section.kind) {
        case 'prose':
            return <ProseSection key={index} title={section.title} body={section.body}/>;
        case 'featureGrid':
            return <FeatureGridSection key={index} {...section}/>;
        case 'steps':
            return <StepsSection key={index} {...section}/>;
        case 'useCases':
            return <UseCasesSection key={index} {...section}/>;
        case 'faq':
            return <FaqSection key={index} {...section}/>;
        case 'cta':
            return <CtaSection key={index} {...section}/>;
        default:
            return null;
    }
};
